// Invitees panel — lives inside the EventDetail modal.
//
// Two faces:
//   1. Organizer view (you can write the event): shows the roster with
//      RSVP statuses and lets you invite + uninvite users.
//   2. Invitee view (you have an inbound invite): shows everyone's
//      statuses plus your own Accept / Decline buttons.
//
// `myInviteStatus` on the event tells us which face to render. The
// underlying RPCs gate on visibility, so the worst case for the wrong
// face is a "permission denied" toast on action.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type CalendarInvitee, type CalendarShareableUser } from "../../lib/rpc";

interface InviteesPanelProps {
  eventId: string;
  myInviteStatus?: string | null;
  meId: string | null;
  notify: (msg: string) => void;
  onChanged: () => void;
}

export function InviteesPanel({ eventId, myInviteStatus, meId, notify, onChanged }: InviteesPanelProps) {
  const [invitees, setInvitees] = useState<CalendarInvitee[]>([]);
  const [users, setUsers] = useState<CalendarShareableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  // Fetch invitees + the shareable-users list. Both feed the picker
  // below — we strip already-invited users from the dropdown so the
  // organizer doesn't try to add the same person twice.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.calendarListInvitees(eventId), api.calendarListShareableUsers()])
      .then(([invs, us]) => {
        if (cancelled) return;
        setInvitees(invs);
        setUsers(us);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId]);

  const refreshInvitees = async () => {
    try {
      const invs = await api.calendarListInvitees(eventId);
      setInvitees(invs);
      onChanged();
    } catch {}
  };

  const invite = async (targetUserId: string) => {
    try {
      await api.calendarInviteUser({ eventId, targetUserId });
      notify(`Invited ${targetUserId}`);
      await refreshInvitees();
      setAdding(false);
    } catch (e: any) {
      notify(e?.message || "Invite failed");
    }
  };

  const uninvite = async (targetUserId: string) => {
    try {
      await api.calendarUninviteUser({ eventId, targetUserId });
      notify(`Uninvited ${targetUserId}`);
      await refreshInvitees();
    } catch (e: any) {
      notify(e?.message || "Uninvite failed");
    }
  };

  const respond = async (status: "accepted" | "declined") => {
    try {
      await api.calendarRespondToInvite({ eventId, status });
      notify(status === "accepted" ? "Accepted" : "Declined");
      await refreshInvitees();
    } catch (e: any) {
      notify(e?.message || "Response failed");
    }
  };

  const inviteeIds = new Set(invitees.map((i) => i.userId));
  // Don't offer to invite yourself or anyone already on the list.
  const addable = users.filter((u) => !inviteeIds.has(u.id) && u.id !== meId);

  if (loading) return null;
  // Nothing to show: no invitees AND we have no RSVP standing. Hide.
  if (invitees.length === 0 && !myInviteStatus) return null;

  return (
    <section className="cal-invitees" aria-label="Invitees">
      <div className="eyebrow cal-invitees-head">
        <Icon name="people" size={11} /> Invitees ({invitees.length})
      </div>
      <ul className="cal-invitees-list">
        {invitees.map((inv) => (
          <li key={inv.userId} className="cal-invitee">
            <span className="cal-manage-avatar">{inv.userId.slice(0, 1).toUpperCase()}</span>
            <span style={{ flex: 1 }}>{inv.userId}{inv.userId === meId && <span className="cal-invitee-you"> (you)</span>}</span>
            <span className="cal-invitee-status" data-status={inv.status}>{inv.status}</span>
            {inv.userId !== meId && (
              <button
                className="icon-btn"
                onClick={() => uninvite(inv.userId)}
                title="Remove invite"
                type="button"
              >
                <Icon name="x" size={11} />
              </button>
            )}
          </li>
        ))}
      </ul>

      {/* Your RSVP — only shows when this event was offered to you. */}
      {myInviteStatus && myInviteStatus !== "accepted" && (
        <div className="cal-rsvp">
          <button className="btn btn-primary" onClick={() => respond("accepted")} type="button">
            <Icon name="check" size={13} /> Accept
          </button>
          {myInviteStatus !== "declined" && (
            <button className="btn" onClick={() => respond("declined")} type="button">
              <Icon name="x" size={13} /> Decline
            </button>
          )}
        </div>
      )}
      {myInviteStatus === "accepted" && (
        <div className="cal-rsvp-status">You've accepted. Change your mind?{" "}
          <button className="btn cal-rsvp-decline" onClick={() => respond("declined")} type="button">
            Decline
          </button>
        </div>
      )}

      {/* Inviter controls — picker + add button. */}
      {!adding && addable.length > 0 && (
        <button
          className="btn cal-invitees-add"
          onClick={() => setAdding(true)}
          type="button"
        >
          <Icon name="plus" size={13} /> Invite someone
        </button>
      )}
      {adding && (
        <div className="cal-invitees-picker">
          {addable.map((u) => (
            <button
              key={u.id}
              className="cal-manage-addable-row"
              onClick={() => invite(u.id)}
              type="button"
            >
              <span className="cal-manage-avatar">{u.id.slice(0, 1).toUpperCase()}</span>
              <span style={{ flex: 1, textAlign: "left" }}>{u.id}</span>
              <Icon name="plus" size={12} />
            </button>
          ))}
          <button className="btn" style={{ marginTop: 6 }} onClick={() => setAdding(false)} type="button">
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}
